import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, Search, FileHeart, FlaskConical, Database, Terminal, Archive,
  Clock, Lock, Download, Activity,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  medical_record: FileHeart,
  clinical_trial: FlaskConical,
  dataset: Database,
  internal_app: Terminal,
  storage: Archive,
};

export default function ResourceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    const session = sessionStorage.getItem(`zt_session_${id}`);
    if (!session) {
      setError("Aucune session d'accès active. Repassez par le catalogue.");
      return;
    }
    api.get(`/resources/${id}/data`, { headers: { "X-Access-Session": session } })
      .then((r) => setData(r.data))
      .catch((err) => {
        const msg = formatApiErrorDetail(err.response?.data?.detail) || "Accès refusé";
        setError(msg);
      });
  }, [id]);

  if (error) {
    return (
      <div className="max-w-lg space-y-4" data-testid="resource-view-error">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">Session</div>
        <h1 className="text-2xl font-semibold tracking-tight">Accès non disponible</h1>
        <Card className="bg-rose-500/5 border-rose-500/20 rounded-sm p-4 text-sm">
          <Lock className="h-4 w-4 text-rose-400 mb-2" />
          {error}
        </Card>
        <Button onClick={() => navigate("/app/catalog")} data-testid="back-to-catalog"
                className="bg-cyan-500 text-black hover:bg-cyan-400">
          <ArrowLeft className="h-4 w-4" /> Retour au catalogue
        </Button>
      </div>
    );
  }

  if (!data) {
    return <div className="text-white/40 font-mono text-sm">Chargement des enregistrements…</div>;
  }

  const { resource, session } = data;
  const Icon = ICONS[resource.type] || Terminal;

  return (
    <div className="space-y-6" data-testid="resource-view-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/app/catalog" data-testid="back-link"
                className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-cyan-400">
            ← catalogue
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <Icon className="h-6 w-6 text-cyan-400" />
            <h1 className="text-3xl font-semibold tracking-tight">{resource.name}</h1>
          </div>
          <div className="mt-1 text-xs text-white/50 font-mono">
            {resource.type} · {resource.site} · {resource.description}
          </div>
        </div>
        <div className="text-right space-y-1">
          <StatusBadge status={resource.sensitivity === "secret" ? "blocked" : resource.sensitivity === "confidential" ? "suspicious" : "trusted"}>
            {resource.sensitivity}
          </StatusBadge>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 flex items-center gap-1 justify-end">
            <Clock className="h-3 w-3" />
            session · exp {new Date(session.expires_at).toLocaleTimeString()}
          </div>
          <div className="text-[10px] font-mono text-white/40">
            policy · {session.policy_name || "n/a"} · risque {session.risk_score}/100
          </div>
        </div>
      </div>

      {/* Search */}
      {(data.records || data.results || data.files) && (
        <div className="relative max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Filtrer les enregistrements…"
                 data-testid="records-filter"
                 className="pl-8 bg-white/[0.03] border-white/10" />
        </div>
      )}

      {resource.type === "medical_record" && <MedicalRecords records={data.records || []} q={q} />}
      {resource.type === "clinical_trial" && <TrialResults results={data.results || []} q={q} />}
      {resource.type === "dataset" && <DatasetFiles files={data.files || []} q={q} />}
      {resource.type === "internal_app" && <AppDashboard widgets={data.widgets || []} />}
      {resource.type === "storage" && <StorageFiles files={data.files || []} q={q} />}
    </div>
  );
}

/* -------- Medical records table -------- */
function MedicalRecords({ records, q }) {
  const filtered = useMemo(() => {
    if (!q) return records;
    const s = q.toLowerCase();
    return records.filter((r) =>
      r.pseudonym?.toLowerCase().includes(s) ||
      r.first_name?.toLowerCase().includes(s) ||
      r.last_name?.toLowerCase().includes(s) ||
      r.disease?.toLowerCase().includes(s) ||
      r.site?.toLowerCase().includes(s) ||
      r.treatment?.toLowerCase().includes(s)
    );
  }, [records, q]);

  const statuses = { "En traitement": "warn", "Rémission": "trusted",
                     "Perdu de vue": "warn", "Guéri": "trusted" };

  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden"
          data-testid="medical-records-table">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-cyan-400" />
          {filtered.length} dossier{filtered.length > 1 ? "s" : ""} patient
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-300">
          Pseudonymisé (RGPD Art. 4.5)
        </div>
      </div>
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0A0A0A] border-b border-white/10">
            <tr className="text-left text-white/50 uppercase tracking-[0.2em] text-[10px]">
              <th className="p-2">Pseudo</th>
              <th className="p-2">Patient</th>
              <th className="p-2">Âge / Sexe</th>
              <th className="p-2">Pathologie</th>
              <th className="p-2">Stade</th>
              <th className="p-2">Traitement</th>
              <th className="p-2">Site</th>
              <th className="p-2">Statut</th>
              <th className="p-2">Dernière visite</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r._id} className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`record-${r._id}`}>
                <td className="p-2 font-mono text-cyan-300">{r.pseudonym}</td>
                <td className="p-2">{r.first_name} {r.last_name.charAt(0)}.</td>
                <td className="p-2 font-mono">{r.age}a · {r.gender}</td>
                <td className="p-2 text-white/80">{r.disease}</td>
                <td className="p-2 font-mono">{r.stage}</td>
                <td className="p-2 text-white/70">{r.treatment} <span className="text-white/40">({r.dosage_mg_kg} mg/kg)</span></td>
                <td className="p-2 text-white/60">{r.site}</td>
                <td className="p-2"><StatusBadge status={statuses[r.status] || "info"}>{r.status}</StatusBadge></td>
                <td className="p-2 text-white/40 font-mono">{new Date(r.last_visit).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function TrialResults({ results, q }) {
  const filtered = useMemo(() => {
    if (!q) return results;
    const s = q.toLowerCase();
    return results.filter((r) =>
      r.trial_id?.toLowerCase().includes(s) ||
      r.participant_code?.toLowerCase().includes(s) ||
      r.arm?.toLowerCase().includes(s) ||
      r.adverse_event?.toLowerCase().includes(s) ||
      r.site?.toLowerCase().includes(s)
    );
  }, [results, q]);

  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden"
          data-testid="trial-results-table">
      <div className="p-3 border-b border-white/10 text-xs font-mono uppercase tracking-[0.2em] text-white/60">
        {filtered.length} résultat{filtered.length > 1 ? "s" : ""} d'essai clinique
      </div>
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0A0A0A] border-b border-white/10">
            <tr className="text-left text-white/50 uppercase tracking-[0.2em] text-[10px]">
              <th className="p-2">Essai</th>
              <th className="p-2">Participant</th>
              <th className="p-2">Bras</th>
              <th className="p-2">Semaine</th>
              <th className="p-2">Efficacité</th>
              <th className="p-2">Événement</th>
              <th className="p-2">Grade</th>
              <th className="p-2">PCR</th>
              <th className="p-2">Site</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r._id} className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`trial-row-${r._id}`}>
                <td className="p-2 font-mono text-cyan-300">{r.trial_id}</td>
                <td className="p-2 font-mono">{r.participant_code}</td>
                <td className="p-2">{r.arm}</td>
                <td className="p-2 font-mono">S{r.week}</td>
                <td className="p-2">
                  <span className={
                    r.efficacy_pct >= 80 ? "text-emerald-300" :
                    r.efficacy_pct >= 60 ? "text-cyan-300" : "text-orange-300"
                  }>{r.efficacy_pct}%</span>
                </td>
                <td className="p-2 text-white/70">{r.adverse_event}</td>
                <td className="p-2 font-mono">G{r.grade}</td>
                <td className="p-2 font-mono">{r.biomarker_pcr}</td>
                <td className="p-2 text-white/60">{r.site}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function DatasetFiles({ files, q }) {
  const filtered = files.filter((f) =>
    !q || f.filename.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4" data-testid="dataset-files">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-3">
        {filtered.length} fichier{filtered.length > 1 ? "s" : ""}
      </div>
      <div className="space-y-2">
        {filtered.map((f) => (
          <div key={f._id} className="flex items-center justify-between border border-white/10 rounded-sm p-3 hover:bg-white/[0.03] transition-colors"
               data-testid={`dataset-file-${f._id}`}>
            <div className="flex items-center gap-3">
              <Database className="h-4 w-4 text-cyan-400" />
              <div>
                <div className="text-sm font-mono">{f.filename}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                  {f.type} · {f.size_mb} MB · SHA256 {f.checksum_sha256.slice(0, 12)}…
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost"
                    onClick={() => toast.info("Téléchargement simulé — jeton de session validé")}
                    className="text-white/60 hover:text-cyan-400 hover:bg-white/5">
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AppDashboard({ widgets }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="app-widgets">
      {widgets.map((w) => (
        <Card key={w._id} className="bg-white/[0.02] border-white/10 rounded-sm p-4">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60">{w.name}</div>
          {w.type === "kpi" && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {Object.entries(w.kpis || {}).map(([k, v]) => (
                <div key={k} className="border border-white/10 rounded-sm p-2">
                  <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">{k}</div>
                  <div className="mt-1 text-xl font-semibold">{v}</div>
                </div>
              ))}
            </div>
          )}
          {w.type === "table_link" && (
            <div className="mt-3 text-sm text-white/70">
              Vue tabulaire agrégée — activez la ressource "Résultats essai clinique Chagas".
            </div>
          )}
          {w.type === "documents" && (
            <div className="mt-3 text-sm text-white/70">
              {w.count} document(s) internes disponibles.
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function StorageFiles({ files, q }) {
  const filtered = files.filter((f) =>
    !q || f.filename.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4" data-testid="storage-files">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-3">
        {filtered.length} document{filtered.length > 1 ? "s" : ""} public{filtered.length > 1 ? "s" : ""}
      </div>
      <div className="space-y-2">
        {filtered.map((f) => (
          <div key={f._id} className="flex items-center justify-between border border-white/10 rounded-sm p-3 hover:bg-white/[0.03] transition-colors">
            <div className="flex items-center gap-3">
              <Archive className="h-4 w-4 text-cyan-400" />
              <div>
                <div className="text-sm font-mono">{f.filename}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                  {f.category} · {f.size_mb} MB
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost"
                    onClick={() => toast.success("Fichier public accessible")}
                    className="text-white/60 hover:text-cyan-400 hover:bg-white/5">
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
